import React, { useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useActiveCampaignByProductId } from 'src/hooks/active-campaign-by-product-id';
import { GridLayout } from 'src/areas/product-collection/components/products/layout-grid';
import { trackKWLPOfferClick, trackKWLPProductClick } from '../tracking';
import { LayoutMode } from 'src/components/layout-buttons/types';
import {
  TrackClickScope,
  trackOfferClickWithRevenue,
} from 'src/globals/tracking/conversion';
import { productPriceClick } from '../../product/actions/product';
import type { Product, Offer } from '.';
import type { Raw as TRaw, Product as TProduct } from '../../../types';

type Props = {
  title: string;
  items: (Product | Offer)[];
};

export const ProductResults: React.FC<Props> = ({ title, items }) => {
  const dispatch = useDispatch();

  // useMemo is needed here to avoid infinite invocations of useActiveCampaignByProductId
  const productIds = useMemo(
    () =>
      items
        .filter((p): p is Product => p.__typename === 'Product')
        .map(p => p.id),
    [items]
  );

  const productsWithActiveCampaign = useActiveCampaignByProductId(
    productIds,
    true
  );

  // We have this conditional logic so that we don't show the ad and the footer ad directly after each other when there aren't enough products to fill the row
  const adsLayout = {
    [LayoutMode.GRID]: {
      panorama_extra: { show: items.length > 20, row: 3 },
      dubbelpanorama1: { show: items.length > 8, row: 3 },
      dubbelpanorama2: { show: true, row: 8 },
    },
    [LayoutMode.LIST]: {
      panorama_extra: { show: false, row: 2 },
      dubbelpanorama1: { show: true, row: 3 },
      dubbelpanorama2: { show: true, row: 8 },
    },
    [LayoutMode.COMPACT_LIST]: {
      panorama_extra: { show: false, row: 2 },
      dubbelpanorama1: { show: true, row: 3 },
      dubbelpanorama2: { show: true, row: 15 },
    },
  };

  // In hope of showing images on Google, we use the 280px variant of the image for the first product
  const firstProduct = items.find(
    (p): p is Product => p.__typename === 'Product'
  );
  if (firstProduct) {
    firstProduct.media.first =
      // Also fall back on empty string
      firstProduct.media.firstLarge || firstProduct.media.first;
  }

  return (
    <GridLayout
      productsWithActiveCampaign={productsWithActiveCampaign}
      isExtraWide
      // This is a faulty assertion, but we're leaving KWLP domain and fixing that as well is out of scope for this cleanup session
      // TODO
      products={items as (TProduct | TRaw)[]}
      adslayoutConfig={adsLayout}
      onClick={productClickHandler}
    />
  );

  function productClickHandler(
    id: string | number,
    _name?: string,
    type?: string
  ): void {
    if (type === 'Offer') {
      const clickedProduct = items.find<Offer>((p): p is Offer => {
        return p.__typename === 'Offer' && p.offerId === id;
      })!;

      const { offerId } = clickedProduct;

      dispatch(
        productPriceClick(null, null, !!clickedProduct.store.featured, 'raw')
      );

      if (offerId) {
        trackKWLPOfferClick(title, offerId);
        trackOfferClickWithRevenue({
          scope: TrackClickScope.KEYWORD_PAGE,
          offerId,
        });
      }
    }
    if (type === 'Product') trackKWLPProductClick(title, id as number);
  }
};
